import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBrain,
    faDumbbell,
    faEdit,
    faHeartbeat,
    faLayerGroup,
    faMicroscope,
    faPlus,
    faProcedures,
    faSyncAlt,
    faTooth,
    faBaby,
    faEye,
    faFemale,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import localForage from "localforage";
import { buildApiUrl, useBaseUrl } from "../../../utils/utils";
import ServiceCenterShell from "./ServiceCenterShell";

interface Service {
    id: number;
    service_id: number;
    service_name: string;
    service_title: string;
    service_subtitle: string;
    service_category: string;
    minimum_rate: number;
    hourly_rate: number;
    is_billable: boolean;
    is_active: boolean;
    clinic_id: number;
    created_at: string;
    updated_at: string;
}

interface AvailableService {
    id: number;
    title: string;
    subtitle: string;
    category: string;
}

interface PaginationMeta {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

interface ServiceSummary {
    active_services: number;
    inactive_services: number;
    billable_services: number;
    average_minimum: number;
    average_hourly: number;
}

const formatCurrency = (value: number) => `KES ${Number(value || 0).toLocaleString()}`;
const emptyPagination = (): PaginationMeta => ({ currentPage: 1, totalPages: 1, totalItems: 0, hasNext: false, hasPrevious: false });
const emptySummary = (): ServiceSummary => ({ active_services: 0, inactive_services: 0, billable_services: 0, average_minimum: 0, average_hourly: 0 });
const readPagination = (pagination: any, fallbackLength: number = 0): PaginationMeta => ({
    currentPage: Number(pagination?.current_page ?? pagination?.currentPage ?? 1),
    totalPages: Number(pagination?.total_pages ?? pagination?.totalPages ?? 1),
    totalItems: Number(pagination?.total_items ?? pagination?.totalItems ?? fallbackLength),
    hasNext: Boolean(pagination?.has_next ?? pagination?.hasNext ?? false),
    hasPrevious: Boolean(pagination?.has_previous ?? pagination?.hasPrevious ?? false),
});

const MyServicesPage: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [services, setServices] = useState<Service[]>([]);
    const [availableServices, setAvailableServices] = useState<AvailableService[]>([]);
    const [servicesPagination, setServicesPagination] = useState<PaginationMeta>(emptyPagination());
    const [serviceSummary, setServiceSummary] = useState<ServiceSummary>(emptySummary());
    const [loading, setLoading] = useState(true);
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [viewMode, setViewMode] = useState<"table" | "cards">("cards");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<"" | "active" | "inactive" | "billable">("");
    const [servicePage, setServicePage] = useState(1);
    const [availableServiceSearch, setAvailableServiceSearch] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingService, setEditingService] = useState<Service | null>(null);
    const [formData, setFormData] = useState({
        service_id: "",
        minimum_rate: "",
        hourly_rate: "",
        is_billable: true,
    });

    useEffect(() => {
        document.title = "My Services - Uzima Healthcare";
        getClinicId();
    }, []);

    useEffect(() => {
        if (!clinicId) return;
        FetchClinicServices(servicePage);
    }, [clinicId, baseUrl, servicePage, search, statusFilter]);

    useEffect(() => {
        setServicePage(1);
    }, [search, statusFilter]);

    useEffect(() => {
        if (!clinicId || (!showAddModal && !showEditModal)) return;
        FetchAvailableServiceOptions();
    }, [clinicId, baseUrl, showAddModal, showEditModal, availableServiceSearch]);

    const getClinicId = async () => {
        try {
            const storedClinicId = await localForage.getItem("clinic_id");
            if (storedClinicId) {
                setClinicId(storedClinicId as number);
                return;
            }
            const loginInfo: any = await localForage.getItem("uzima_login_info");
            if (loginInfo?.clinic_id) {
                setClinicId(loginInfo.clinic_id);
                return;
            }
            const staffDetails = localStorage.getItem("staffDetails");
            if (staffDetails) {
                const parsed = JSON.parse(staffDetails);
                if (parsed?.clinic_id) setClinicId(parsed.clinic_id);
            }
        } catch (error) {
            console.error("Error getting clinic ID:", error);
        }
    };

    const FetchClinicServices = async (page: number = 1) => {
        if (!clinicId) return;
        try {
            setLoading(true);
            const params = new URLSearchParams({ page: String(page), page_size: '12' });
            if (search) params.set('search', search);
            if (statusFilter === 'active' || statusFilter === 'inactive') params.set('status', statusFilter);
            if (statusFilter === 'billable') params.set('billable', 'true');
            const response = await fetch(buildApiUrl(baseUrl, `facilities/services/clinic-services/${clinicId}/?${params.toString()}`));
            if (!response.ok) {
                setServices([]);
                setServicesPagination(emptyPagination());
                setServiceSummary(emptySummary());
                return;
            }
            const data = await response.json();
            const payload = data.data || {};
            const servicesData = payload.services || [];
            setServices(Array.isArray(servicesData) ? servicesData : []);
            setServicesPagination(readPagination(payload.pagination, servicesData.length));
            setServiceSummary(payload.summary || emptySummary());
        } catch (error) {
            console.error("Error fetching clinic services:", error);
            setServices([]);
            setServicesPagination(emptyPagination());
            setServiceSummary(emptySummary());
        } finally {
            setLoading(false);
        }
    };

    const FetchAvailableServiceOptions = async () => {
        if (!clinicId) return;
        try {
            const params = new URLSearchParams({ page: '1', page_size: '50' });
            if (availableServiceSearch) params.set('search', availableServiceSearch);
            const response = await fetch(buildApiUrl(baseUrl, `facilities/services/available/${clinicId}/?${params.toString()}`));
            if (!response.ok) {
                setAvailableServices([]);
                return;
            }
            const data = await response.json();
            const rawServices = Array.isArray(data.data?.available_services) ? data.data.available_services : [];
            setAvailableServices(
                rawServices.map((service: any) => ({
                    id: service.id,
                    title: service.title || service.name,
                    subtitle: service.subtitle || "",
                    category: service.category || "General",
                }))
            );
        } catch (error) {
            console.error("Error fetching available services:", error);
            setAvailableServices([]);
        }
    };

    const UpdateServiceFormInput = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? (event.target as HTMLInputElement).checked : value,
        }));
    };

    const resetForm = () => {
        setFormData({
            service_id: "",
            minimum_rate: "",
            hourly_rate: "",
            is_billable: true,
        });
        setAvailableServiceSearch("");
        setEditingService(null);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        resetForm();
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        resetForm();
    };

    const CreateClinicServiceListing = async () => {
        if (!clinicId || !formData.service_id || !formData.minimum_rate || !formData.hourly_rate) {
            Swal.fire({ icon: "warning", title: "Missing information", text: "Please complete all required fields." });
            return;
        }
        try {
            const response = await fetch(buildApiUrl(baseUrl, "facilities/services/create/"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...formData,
                    clinic_id: clinicId,
                    minimum_rate: parseFloat(formData.minimum_rate),
                    hourly_rate: parseFloat(formData.hourly_rate),
                }),
            });
            const data = await response.json();
            if (data.status !== "success") throw new Error(data.message || "Failed to add service");
            Swal.fire({ icon: "success", title: "Service added", text: "Your service has been added successfully.", timer: 1800, showConfirmButton: false });
            closeAddModal();
            FetchClinicServices(servicePage);
            FetchAvailableServiceOptions();
        } catch (error) {
            console.error("Error adding service:", error);
            Swal.fire({ icon: "error", title: "Add failed", text: "Failed to add service. Please try again." });
        }
    };

    const SaveClinicServiceListingChanges = async () => {
        if (!clinicId || !editingService || !formData.service_id || !formData.minimum_rate || !formData.hourly_rate) {
            Swal.fire({ icon: "warning", title: "Missing information", text: "Please complete all required fields." });
            return;
        }
        try {
            const response = await fetch(buildApiUrl(baseUrl, `facilities/services/update/${editingService.id}/`), {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...formData,
                    clinic_id: clinicId,
                    minimum_rate: parseFloat(formData.minimum_rate),
                    hourly_rate: parseFloat(formData.hourly_rate),
                }),
            });
            const data = await response.json();
            if (data.status !== "success") throw new Error(data.message || "Failed to update service");
            Swal.fire({ icon: "success", title: "Service updated", text: "Your service changes were saved.", timer: 1800, showConfirmButton: false });
            closeEditModal();
            FetchClinicServices(servicePage);
        } catch (error) {
            console.error("Error updating service:", error);
            Swal.fire({ icon: "error", title: "Update failed", text: "Failed to update service. Please try again." });
        }
    };

    const ToggleClinicServiceStatus = async (serviceId: number) => {
        try {
            const response = await fetch(buildApiUrl(baseUrl, `facilities/services/toggle/${serviceId}/`), {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
            });
            const data = await response.json();
            if (data.status !== "success") throw new Error(data.message || "Failed to update status");
            Swal.fire({ icon: "success", title: "Status updated", text: "Service visibility updated.", timer: 1400, showConfirmButton: false });
            FetchClinicServices(servicePage);
            FetchAvailableServiceOptions();
        } catch (error) {
            console.error("Error toggling service:", error);
            Swal.fire({ icon: "error", title: "Update failed", text: "Failed to update service status. Please try again." });
        }
    };

    const openEditModal = (service: Service) => {
        setEditingService(service);
        setFormData({
            service_id: service.service_id.toString(),
            minimum_rate: service.minimum_rate.toString(),
            hourly_rate: service.hourly_rate.toString(),
            is_billable: service.is_billable,
        });
        setShowEditModal(true);
    };

    const getServiceIcon = (category: string) => {
        const lowerCategory = (category || "").toLowerCase();
        if (lowerCategory.includes("cardio") || lowerCategory.includes("heart")) return faHeartbeat;
        if (lowerCategory.includes("neuro") || lowerCategory.includes("brain")) return faBrain;
        if (lowerCategory.includes("dental") || lowerCategory.includes("tooth")) return faTooth;
        if (lowerCategory.includes("pediatric") || lowerCategory.includes("child")) return faBaby;
        if (lowerCategory.includes("lab") || lowerCategory.includes("test")) return faMicroscope;
        if (lowerCategory.includes("physio") || lowerCategory.includes("therapy")) return faDumbbell;
        if (lowerCategory.includes("gyneco") || lowerCategory.includes("women")) return faFemale;
        if (lowerCategory.includes("ophthal") || lowerCategory.includes("eye")) return faEye;
        if (lowerCategory.includes("surgery") || lowerCategory.includes("operation")) return faProcedures;
        return faLayerGroup;
    };

    const visibleServices = useMemo(() => services, [services]);

    const renderStatus = (service: Service) => (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className={`sc-badge-status ${service.is_active ? "agreed" : "rejected"}`}>{service.is_active ? "Active" : "Inactive"}</span>
            <span className={`sc-chip ${service.is_billable ? "" : "soft"}`}>{service.is_billable ? "Billable" : "Non-billable"}</span>
        </div>
    );

    const renderServiceCards = () => (
        <div className="sc-grid">
            {visibleServices.map((service) => (
                <div className="sc-card" key={service.id}>
                    <div className="sc-card-body">
                        <div className="sc-card-row">
                            <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flex: 1 }}>
                                <div className="sc-stat-icon blue" style={{ marginBottom: 0 }}>
                                    <FontAwesomeIcon icon={getServiceIcon(service.service_category)} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h5 className="sc-card-title">{service.service_title}</h5>
                                    <p className="sc-card-desc">{service.service_subtitle || "No subtitle available"}</p>
                                </div>
                            </div>
                            {renderStatus(service)}
                        </div>
                        <div className="sc-card-meta" style={{ marginTop: 14 }}>
                            <span><i className="bx bx-category"></i> {service.service_category || "General"}</span>
                            <span><i className="bx bx-wallet"></i> Min {formatCurrency(service.minimum_rate)}</span>
                            <span><i className="bx bx-time-five"></i> Hourly {formatCurrency(service.hourly_rate)}</span>
                            <span><i className="bx bx-calendar"></i> {new Date(service.created_at).toLocaleDateString()}</span>
                        </div>
                    </div>
                    <div className="sc-card-foot">
                        <span className="sc-chip soft">Service #{service.service_id}</span>
                        <div style={{ display: "flex", gap: 8 }}>
                            <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => openEditModal(service)}>
                                <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                            <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => ToggleClinicServiceStatus(service.id)}>
                                <FontAwesomeIcon icon={faSyncAlt} /> {service.is_active ? "Disable" : "Enable"}
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderServiceTable = () => (
        <div className="sc-panel">
            <div className="sc-panel-head">
                <div>
                    <h3>Service catalogue</h3>
                    <p>View and manage all service entries available from your clinic.</p>
                </div>
            </div>
            <div className="sc-panel-body">
                <div className="sc-table-wrap">
                    <table className="sc-table">
                        <thead>
                            <tr>
                                <th>Service</th>
                                <th>Category</th>
                                <th>Minimum</th>
                                <th>Hourly</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleServices.map((service) => (
                                <tr key={service.id}>
                                    <td>
                                        <div>
                                            <strong>{service.service_title}</strong>
                                            <div style={{ fontSize: "0.74rem", color: "var(--uzima-shell-text-muted)", marginTop: 4 }}>{service.service_subtitle || "No subtitle available"}</div>
                                        </div>
                                    </td>
                                    <td>{service.service_category || "General"}</td>
                                    <td>{formatCurrency(service.minimum_rate)}</td>
                                    <td>{formatCurrency(service.hourly_rate)}</td>
                                    <td>{renderStatus(service)}</td>
                                    <td>
                                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                                            <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => openEditModal(service)}>
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => ToggleClinicServiceStatus(service.id)}>
                                                <FontAwesomeIcon icon={faSyncAlt} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const modalTitle = editingService ? "Edit service" : "Add service";

    const renderServiceModal = (isEditing: boolean) => (
        <div className="sc-modal-overlay" onClick={isEditing ? closeEditModal : closeAddModal}>
            <div className="sc-modal" onClick={(event) => event.stopPropagation()}>
                <div className="sc-modal-header">
                    <h2>{modalTitle}</h2>
                    <button className="sc-modal-close" onClick={isEditing ? closeEditModal : closeAddModal}>
                        <i className="bx bx-x"></i>
                    </button>
                </div>
                <div className="sc-modal-body">
                    <div className="sc-form-group">
                        <label>Search services</label>
                        <input className="sc-input" value={availableServiceSearch} onChange={(event) => setAvailableServiceSearch(event.target.value)} placeholder="Search available services..." />
                    </div>
                    <div className="sc-form-group">
                        <label>Service <span>*</span></label>
                        <select className="sc-input" name="service_id" value={formData.service_id} onChange={UpdateServiceFormInput}>
                            <option value="">Select service</option>
                            {availableServices.map((service) => (
                                <option key={service.id} value={service.id}>{service.title}{service.subtitle ? ` - ${service.subtitle}` : ""}</option>
                            ))}
                        </select>
                    </div>
                    <div className="sc-form-row">
                        <div className="sc-form-group">
                            <label>Minimum rate <span>*</span></label>
                            <input className="sc-input" type="number" min="0" step="0.01" name="minimum_rate" value={formData.minimum_rate} onChange={UpdateServiceFormInput} placeholder="0" />
                        </div>
                        <div className="sc-form-group">
                            <label>Hourly rate <span>*</span></label>
                            <input className="sc-input" type="number" min="0" step="0.01" name="hourly_rate" value={formData.hourly_rate} onChange={UpdateServiceFormInput} placeholder="0" />
                        </div>
                    </div>
                    <label className="sc-toggle">
                        <input type="checkbox" name="is_billable" checked={formData.is_billable} onChange={UpdateServiceFormInput} />
                        <span>Make this service billable</span>
                    </label>
                </div>
                <div className="sc-modal-footer">
                    <button className="sc-btn sc-btn-ghost" onClick={isEditing ? closeEditModal : closeAddModal}>Cancel</button>
                    <button className="sc-btn sc-btn-primary" onClick={isEditing ? SaveClinicServiceListingChanges : CreateClinicServiceListing}>
                        <FontAwesomeIcon icon={isEditing ? faEdit : faPlus} /> {isEditing ? "Save changes" : "Add service"}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <ServiceCenterShell
                title="My Services"
                description="Manage your clinic catalogue, rates, availability, and billable services from one place."
                activeTab="my-services"
                action={
                    <button className="sc-btn sc-btn-primary" onClick={() => setShowAddModal(true)}>
                        <FontAwesomeIcon icon={faPlus} /> Add Service
                    </button>
                }
            >
                {!clinicId ? (
                    <div className="sc-empty">
                        <i className="bx bx-buildings"></i>
                        <h3>No clinic associated</h3>
                        <p>Please ensure you are logged in with a valid clinic account before managing services.</p>
                        <button className="sc-btn sc-btn-outline" onClick={getClinicId}><FontAwesomeIcon icon={faSyncAlt} /> Refresh clinic context</button>
                    </div>
                ) : (
                    <>
                        <div className="sc-stats">
                            <div className="sc-stat">
                                <div className="sc-stat-icon teal"><i className="bx bx-layer-group"></i></div>
                                <div className="sc-stat-value">{servicesPagination.totalItems}</div>
                                <div className="sc-stat-label">Total Services</div>
                            </div>
                            <div className="sc-stat">
                                <div className="sc-stat-icon blue"><i className="bx bx-check-shield"></i></div>
                                <div className="sc-stat-value">{serviceSummary.active_services}</div>
                                <div className="sc-stat-label">Active Listings</div>
                            </div>
                            <div className="sc-stat">
                                <div className="sc-stat-icon amber"><i className="bx bx-wallet"></i></div>
                                <div className="sc-stat-value">{formatCurrency(serviceSummary.average_minimum)}</div>
                                <div className="sc-stat-label">Average Minimum</div>
                            </div>
                            <div className="sc-stat">
                                <div className="sc-stat-icon violet"><i className="bx bx-time-five"></i></div>
                                <div className="sc-stat-value">{formatCurrency(serviceSummary.average_hourly)}</div>
                                <div className="sc-stat-label">Average Hourly</div>
                            </div>
                            <div className="sc-stat">
                                <div className="sc-stat-icon rose"><i className="bx bx-receipt"></i></div>
                                <div className="sc-stat-value">{serviceSummary.billable_services}</div>
                                <div className="sc-stat-label">Billable Services</div>
                            </div>
                        </div>

                        <div className="sc-toolbar">
                            <div className="sc-search">
                                <i className="bx bx-search"></i>
                                <input placeholder="Search services, subtitles, or categories..." value={search} onChange={(event) => setSearch(event.target.value)} />
                            </div>
                            <select className="sc-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as "" | "active" | "inactive" | "billable")}>
                                <option value="">All statuses</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="billable">Billable</option>
                            </select>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button className={`sc-btn ${viewMode === "cards" ? "sc-btn-primary" : "sc-btn-outline"}`} onClick={() => setViewMode("cards")}>
                                    <i className="bx bx-grid-alt"></i> Cards
                                </button>
                                <button className={`sc-btn ${viewMode === "table" ? "sc-btn-primary" : "sc-btn-outline"}`} onClick={() => setViewMode("table")}>
                                    <i className="bx bx-table"></i> Table
                                </button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="sc-spinner"></div>
                        ) : visibleServices.length === 0 ? (
                            <div className="sc-empty">
                                <i className="bx bx-folder-open"></i>
                                <h3>{servicesPagination.totalItems === 0 ? "No services yet" : "No matching services"}</h3>
                                <p>{servicesPagination.totalItems === 0 ? "Start by publishing your first clinic service." : "Try a different search or filter combination."}</p>
                                {servicesPagination.totalItems === 0 && <button className="sc-btn sc-btn-primary" onClick={() => setShowAddModal(true)}><FontAwesomeIcon icon={faPlus} /> Add your first service</button>}
                            </div>
                        ) : (
                            <>
                                {viewMode === "cards" ? renderServiceCards() : renderServiceTable()}
                                {servicesPagination.totalPages > 1 && (
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 20 }}>
                                        <button className="sc-btn sc-btn-ghost" disabled={!servicesPagination.hasPrevious} onClick={() => setServicePage((prev) => prev - 1)}>Prev</button>
                                        <span className="sc-chip soft">Page {servicesPagination.currentPage} of {servicesPagination.totalPages}</span>
                                        <button className="sc-btn sc-btn-ghost" disabled={!servicesPagination.hasNext} onClick={() => setServicePage((prev) => prev + 1)}>Next</button>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </ServiceCenterShell>
            {showAddModal && renderServiceModal(false)}
            {showEditModal && editingService && renderServiceModal(true)}
        </>
    );
};

export default MyServicesPage;
